/*
 * Purpose: Shared workflow helpers (labels, persistence, starter workflow)
 * Last modified: 2026-06-25 | Agent
 * Completeness: 95
 */
import type { Node, Workflow } from '@/stores/workflowStore';
import { getBlockTypeLabel } from '@/lib/blockTypes';
import { getContextualDisplayName } from '@/lib/blockContext';
import { createMissionStartNode, createMissionGoalNode } from '@/lib/missionBlocks';

export function getNodeLabel(node: Node): string {
  const typeFallback = getBlockTypeLabel(node.type, node.subtype);

  if (node.type === 'goal' && node.subtype === 'outcome') {
    const outcome = String(node.config?.outcome ?? '').trim();
    if (outcome) {
      return outcome.length > 48 ? `${outcome.slice(0, 47)}…` : outcome;
    }
  }

  return getContextualDisplayName(node.config ?? {}, typeFallback);
}

export function persistWorkflows(workflows: Workflow[]): void {
  localStorage.setItem('workflows', JSON.stringify(workflows));
}

export function parseWorkflowDates(workflow: Workflow): Workflow {
  return {
    ...workflow,
    createdAt: new Date(workflow.createdAt),
    updatedAt: new Date(workflow.updatedAt),
  };
}

export function createStarterWorkflow(): Workflow {
  const start = createMissionStartNode(80, 80);
  const goal = createMissionGoalNode('Run this workflow successfully', 80, 180);
  const trigger = {
    id: 'trigger-manual',
    type: 'trigger' as const,
    subtype: 'manual' as const,
    config: { name: 'Manual Trigger' },
    x: 300,
    y: 180,
  };
  const action = {
    id: 'action-log',
    type: 'action' as const,
    subtype: 'log' as const,
    config: { name: 'Log Result', message: 'Workflow executed successfully!' },
    x: 520,
    y: 180,
  };

  return {
    id: 'starter-quick-start',
    name: 'Quick Start Workflow',
    nodes: [start, goal, trigger, action],
    edges: [
      { id: 'edge-start-goal', from: start.id, to: goal.id },
      { id: 'edge-goal-trigger', from: goal.id, to: trigger.id },
      { id: 'edge-starter', from: trigger.id, to: action.id },
    ],
    createdAt: new Date(),
    updatedAt: new Date(),
    missionMeta: { allowDynamicMap: true, mapLocked: false },
  };
}

export function loadWorkflowsFromStorage(): Workflow[] {
  const stored = localStorage.getItem('workflows');
  if (stored) {
    try {
      const parsed = JSON.parse(stored) as Workflow[];
      if (Array.isArray(parsed) && parsed.length > 0) {
        return parsed.map(parseWorkflowDates);
      }
    } catch {
      console.warn('Failed to load workflows from storage');
    }
  }

  const starter = createStarterWorkflow();
  persistWorkflows([starter]);
  return [starter];
}
